import React, { useCallback, useState } from 'react';
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MarkerType,
} from '@xyflow/react';
import type { Node, Edge, Connection, EdgeChange } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Alert, Box, IconButton } from '@mui/joy';
import { Plus as AddIcon } from '@phosphor-icons/react';

import type {
  Automation,
  AutomationTrigger,
  AutomationAction,
  AutomationModifier,
} from '../../types/automations';
import TriggerNode from './nodes/TriggerNode';
import ActionNode from './nodes/ActionNode';
import ModifierNode from './nodes/ModifierNode';
import DeletableEdge from './edges/DeletableEdge';
import AddableEdge from './edges/AddableEdge';
import AddWorkflowItemModal from './AddWorkflowItemModal';
import WorkflowItemConfigModal from './WorkflowItemConfigModal';
import MovePositionModal from './MovePositionModal';

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  modifier: ModifierNode,
};

const edgeTypes = {
  deletable: DeletableEdge,
  addable: AddableEdge,
};

interface WorkflowCanvasProps {
  automation: Automation;
  onUpdate: (automation: Automation) => void;
}

interface PendingItem {
  type: 'trigger' | 'action' | 'modifier';
  specificType: string;
  afterNodeId?: string; // Insert after this node
  insertionIndex?: number;
}

export default function WorkflowCanvas({ automation, onUpdate }: WorkflowCanvasProps) {
  const [graphError, setGraphError] = useState('');
  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState<PendingItem | null>(null);
  const [pendingEdgeId, setPendingEdgeId] = useState<string | null>(null);
  const [allowedTypes, setAllowedTypes] = useState<('trigger' | 'action' | 'modifier')[]>(['trigger', 'action', 'modifier']);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveModalData, setMoveModalData] = useState<{
    item: AutomationAction | AutomationModifier;
    type: 'action' | 'modifier';
    moveAll: boolean;
  } | null>(null);
  const [editingItem, setEditingItem] = useState<{
    type: 'trigger' | 'action' | 'modifier';
    item: AutomationTrigger | AutomationAction | AutomationModifier;
    index: number;
  } | null>(null);
  const [afterNodeId, setAfterNodeId] = useState<string | undefined>();

  const handleEditItem = useCallback(
    (
      type: 'trigger' | 'action' | 'modifier',
      item: AutomationTrigger | AutomationAction | AutomationModifier
    ) => {
      // Find the index of this item
      let index = -1;
      if (type === 'trigger') {
        index = automation.triggers.findIndex((t) => t === item);
      } else if (type === 'action') {
        index = automation.actions.findIndex((a) => a === item);
      } else if (type === 'modifier') {
        index = automation.modifiers?.findIndex((m) => m === item) ?? -1;
      }

      if (index !== -1) {
        setEditingItem({ type, item, index });
        setConfigModalOpen(true);
      }
    },
    [automation]
  );

  // Handle move operations
  const handleMoveItem = useCallback(
    (item: AutomationAction | AutomationModifier, type: 'action' | 'modifier', moveAll: boolean) => {
      setMoveModalData({ item, type, moveAll });
      setMoveModalOpen(true);
    },
    []
  );

  const handleMoveConfirm = useCallback(
    (newPosition: number) => {
      if (!moveModalData) return;

      const all = [...automation.actions, ...(automation.modifiers || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const { item, moveAll } = moveModalData;
      const moved = all.filter(step => moveAll ? (step.order ?? 0) >= (item.order ?? 0) : step === item);
      const remaining = all.filter(step => !moved.includes(step));
      const before = remaining.findIndex(step => (step.order ?? 0) >= newPosition);
      remaining.splice(before < 0 ? remaining.length : before, 0, ...moved);
      const positions = new Map(remaining.map((step, index) => [step, index]));
      const invalid = remaining.some(step => {
        if (step.parent_id == null) return false;
        const parent = automation.modifiers?.find(modifier => modifier.id === step.parent_id);
        return !parent || positions.get(parent)! >= positions.get(step)!;
      });
      if (invalid) { setGraphError('Keep each branch after its If/Else step. Move the branch steps together.'); return; }
      setGraphError('');
      onUpdate({ ...automation,
        actions: automation.actions.map(step => ({ ...step, order: positions.get(step)! })),
        modifiers: automation.modifiers?.map(step => ({ ...step, order: positions.get(step)! })),
      });
      setMoveModalOpen(false);
      setMoveModalData(null);
    },
    [automation, onUpdate, moveModalData]
  );

  const handleDeleteItem = useCallback(
    (
      type: 'trigger' | 'action' | 'modifier',
      item: AutomationTrigger | AutomationAction | AutomationModifier
    ) => {
      const newAutomation = { ...automation };

      if (type === 'trigger') {
        newAutomation.triggers = automation.triggers.filter((t) => t !== item);
      } else if (type === 'action') {
        newAutomation.actions = automation.actions.filter((a) => a !== item);
      } else if (type === 'modifier') {
        const removed = new Set([item.id]);
        let changed = true;
        while (changed) {
          changed = false;
          for (const modifier of automation.modifiers || []) {
            if (modifier.parent_id != null && removed.has(modifier.parent_id) && !removed.has(modifier.id)) {
              removed.add(modifier.id); changed = true;
            }
          }
        }
        newAutomation.modifiers = automation.modifiers?.filter(modifier => !removed.has(modifier.id));
        newAutomation.actions = automation.actions.filter(action => action.parent_id == null || !removed.has(action.parent_id));
      }

      onUpdate(newAutomation);
    },
    [automation, onUpdate]
  );

  // Handle adding a new node from edge button
  const handleEdgeAdd = useCallback(
    (edgeId: string) => {
      // For trigger edges, we want to insert at position 0
      if (edgeId.startsWith('e-trigger-')) {
        setPendingEdgeId('e-0'); // Insert at the beginning of workflow
      } else {
        setPendingEdgeId(edgeId);
      }
      // Only allow actions and modifiers when inserting between steps (not triggers)
      setAllowedTypes(['action', 'modifier']);
      setAddModalOpen(true);
    },
    []
  );
  
  // Handle edge deletion from button
  const handleEdgeDelete = useCallback(
    (edgeId: string) => {

      // Edge ID format: "e-{sourceNodeId}-{targetNodeId}"
      // Example: "e-trigger-0-action-1" or "e-action-0-action-1"
      // We need to extract the target node ID which starts with trigger/action/modifier

      // Find where the target node type begins (after the source node)
      const targetTypeMatch = edgeId.match(/-(trigger|action|modifier)-[^-]+$/);
      if (!targetTypeMatch) {
        return;
      }

      // Extract the full target node ID
      const targetNodeId = targetTypeMatch[0].substring(1); // Remove leading dash

      // Parse target node ID to get type and ID
      const parts = targetNodeId.match(/^(trigger|action|modifier)-(.+)$/);
      if (!parts) {
        return;
      }

      const [, targetType, targetId] = parts;

      const newAutomation = { ...automation };

      if (targetType === 'action') {
        const targetActionIndex = automation.actions.findIndex(
          (a) => String(a.id || automation.actions.indexOf(a)) === targetId
        );


        if (targetActionIndex !== -1) {
          const updatedActions = [...newAutomation.actions];

          // Clear parent_id and branch_type
          updatedActions[targetActionIndex] = {
            ...updatedActions[targetActionIndex],
            parent_id: null,
            branch_type: null,
          };

          newAutomation.actions = updatedActions;
          onUpdate(newAutomation);
        }
      } else if (targetType === 'modifier' && automation.modifiers) {
        const targetModifierIndex = automation.modifiers.findIndex(
          (m) => String(m.id || automation.modifiers?.indexOf(m)) === targetId
        );


        if (targetModifierIndex !== -1) {
          const updatedModifiers = [...(newAutomation.modifiers || [])];

          // Clear parent_id and branch_type
          updatedModifiers[targetModifierIndex] = {
            ...updatedModifiers[targetModifierIndex],
            parent_id: null,
            branch_type: null,
          };

          newAutomation.modifiers = updatedModifiers;
          onUpdate(newAutomation);
        }
      }
    },
    [automation, onUpdate]
  );

  // Helper to build node ID from item
  const getNodeId = useCallback((type: string, item: AutomationTrigger | AutomationAction | AutomationModifier, index: number) => {
    return `${type}-${item.id || index}`;
  }, []);

  // Convert automation data to React Flow nodes/edges
  React.useEffect(() => {
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];
    
    // Layout constants
    const triggerSectionY = 50;
    const triggerSpacing = 200; // Reduced spacing
    const workflowStartY = 250;
    const ySpacing = 150;
    const xCenter = 400;

    // Calculate trigger section dimensions based on number of triggers
    const numTriggers = automation.triggers.length;
    const triggerSectionWidth = Math.max(700, (numTriggers * triggerSpacing) + 200);
    const triggerSectionX = Math.max(50, xCenter - (triggerSectionWidth / 2));

    // Add background for trigger section
    newNodes.push({
      id: 'trigger-section-bg',
      type: 'group',
      position: { x: triggerSectionX, y: 0 },
      style: {
        width: triggerSectionWidth,
        height: 180,
        backgroundColor: 'rgba(76, 175, 80, 0.05)',
        border: '2px dashed rgba(76, 175, 80, 0.3)',
        borderRadius: 8,
        padding: 20,
      },
      data: { label: 'Triggers (Required)' },
      selectable: false,
      draggable: false,
    });

    // Add trigger nodes - center them within the section
    const totalTriggersWidth = (numTriggers - 1) * triggerSpacing;
    const triggerStartX = xCenter - (totalTriggersWidth / 2);
    
    automation.triggers.forEach((trigger, idx) => {
      newNodes.push({
        id: getNodeId('trigger', trigger, idx),
        type: 'trigger',
        position: { x: triggerStartX + (idx * triggerSpacing), y: triggerSectionY },
        draggable: false, // Make trigger nodes non-draggable
        data: {
          trigger,
          onEdit: () => handleEditItem('trigger', trigger),
          onDelete: () => handleDeleteItem('trigger', trigger),
        },
      });
    });

    let yPosition = workflowStartY;
    
    // Combine and sort all workflow items by order
    const workflowItems: Array<{item: AutomationAction | AutomationModifier, type: 'action' | 'modifier', index: number}> = [
      ...automation.actions.map((a, idx) => ({ item: a, type: 'action' as const, index: idx })),
      ...(automation.modifiers || []).map((m, idx) => ({ item: m, type: 'modifier' as const, index: idx }))
    ].sort((a, b) => (a.item.order || 0) - (b.item.order || 0));

    // Add nodes for all workflow items in order
    workflowItems.forEach((workflowItem, orderIndex) => {
      const { item, type, index } = workflowItem;
      const nodeId = getNodeId(type, item, index);
      
      if (type === 'action') {
        const action = item as AutomationAction;
        newNodes.push({
          id: nodeId,
          type: 'action',
          position: { x: xCenter, y: yPosition },
          draggable: false, // Make node non-draggable
          data: {
            action,
            onEdit: () => handleEditItem('action', action),
            onDelete: () => handleDeleteItem('action', action),
            onMove: () => handleMoveItem(action, 'action', false),
            onMoveAll: () => handleMoveItem(action, 'action', true),
          },
        });
      } else {
        const modifier = item as AutomationModifier;
        newNodes.push({
          id: nodeId,
          type: 'modifier',
          position: { x: xCenter, y: yPosition },
          draggable: false, // Make node non-draggable
          data: {
            modifier,
            onEdit: () => handleEditItem('modifier', modifier),
            onDelete: () => handleDeleteItem('modifier', modifier),
            onMove: () => handleMoveItem(modifier, 'modifier', false),
            onMoveAll: () => handleMoveItem(modifier, 'modifier', true),
          },
        });
      }
      
      yPosition += ySpacing;
    });

    workflowItems.forEach((entry, index) => {
      const targetId = getNodeId(entry.type, entry.item, entry.index);
      const parentId = entry.item.parent_id ?? null;
      const branch = entry.item.branch_type ?? null;
      const siblings = workflowItems.slice(0, index).filter(previous =>
        (previous.item.parent_id ?? null) === parentId && (previous.item.branch_type ?? null) === branch);
      const previous = siblings[siblings.length - 1];
      if (parentId != null) {
        const visual = newNodes.find(node => node.id === targetId);
        if (visual) visual.position.x += branch === 'true' ? -220 : 220;
      }
      if (previous) {
        const modifier = previous.type === 'modifier' ? previous.item as AutomationModifier : undefined;
        if (modifier?.modifier_type === 'end') return;
        newEdges.push({ id: `e-${index}`, source: getNodeId(previous.type, previous.item, previous.index),
          sourceHandle: modifier?.modifier_type === 'if_else' ? 'continue' : undefined,
          label: modifier?.modifier_type === 'if_else' ? 'After selected branch' : undefined,
          target: targetId, type: 'addable', markerEnd: { type: MarkerType.ArrowClosed }, data: { onAdd: handleEdgeAdd } });
      } else if (parentId != null) {
        const parentIndex = automation.modifiers?.findIndex(item => item.id === parentId) ?? -1;
        const parent = automation.modifiers?.[parentIndex];
        if (parent) newEdges.push({ id: `branch-${targetId}`, source: getNodeId('modifier', parent, parentIndex),
          sourceHandle: branch, target: targetId, label: branch === 'true' ? 'Yes' : 'No', markerEnd: { type: MarkerType.ArrowClosed } });
      } else {
        automation.triggers.forEach((trigger, triggerIndex) => newEdges.push({ id: `e-trigger-${triggerIndex}`,
          source: getNodeId('trigger', trigger, triggerIndex), target: targetId, type: 'addable',
          markerEnd: { type: MarkerType.ArrowClosed }, data: { onAdd: handleEdgeAdd } }));
      }
    });

    setNodes(newNodes);
    setEdges(newEdges);
  }, [automation, handleEditItem, handleDeleteItem, handleMoveItem, handleEdgeAdd, handleEdgeDelete, setNodes, setEdges, getNodeId]);

  // Cleanup effect to ensure modals are closed when component unmounts
  React.useEffect(() => {
    return () => {
      // Reset all modal states on unmount
      setAddModalOpen(false);
      setConfigModalOpen(false);
      setMoveModalOpen(false);
      setPendingItem(null);
      setEditingItem(null);
      setPendingEdgeId(null);
      setMoveModalData(null);
    };
  }, []);

  const onConnect = useCallback((connection: Connection) => {
    const source = nodes.find(node => node.id === connection.source);
    const target = nodes.find(node => node.id === connection.target);
    if (!source || !target || target.type === 'trigger') return;
    const modifier = source.data.modifier as AutomationModifier | undefined;
    if (modifier?.modifier_type !== 'if_else' || !['true', 'false'].includes(connection.sourceHandle || '')) return;
    const targetItem = (target.data.action || target.data.modifier) as AutomationAction | AutomationModifier;
    if ((targetItem.order ?? 0) <= (modifier.order ?? 0)) return;
    const link = { parent_id: modifier.id, branch_type: connection.sourceHandle as 'true' | 'false' };
    onUpdate({ ...automation,
      actions: automation.actions.map(item => item === targetItem ? { ...item, ...link } : item),
      modifiers: automation.modifiers?.map(item => item === targetItem ? { ...item, ...link } : item),
    });
  }, [automation, nodes, onUpdate]);

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // Handle edge deletions
      const removedEdges = changes.filter((change) => change.type === 'remove');

      if (removedEdges.length > 0) {
        const newAutomation = { ...automation };
        const updatedActions = [...newAutomation.actions];
        const updatedModifiers = [...(newAutomation.modifiers || [])];

        removedEdges.forEach((change) => {
          const edgeId = change.id;
          const branchTarget = edgeId.match(/^branch-(action|modifier)-(-?\d+)$/);
          if (branchTarget) {
            const items = branchTarget[1] === 'action' ? updatedActions : updatedModifiers;
            const index = items.findIndex(item => item.id === Number(branchTarget[2]));
            if (index >= 0) items[index] = { ...items[index], parent_id: null, branch_type: null };
            return;
          }

          // Find where the target node type begins (after the source node)
          const targetTypeMatch = edgeId.match(/-(trigger|action|modifier)-[^-]+$/);
          if (!targetTypeMatch) return;

          // Extract the full target node ID
          const targetNodeId = targetTypeMatch[0].substring(1); // Remove leading dash

          // Parse target node ID to get type and ID
          const parts = targetNodeId.match(/^(trigger|action|modifier)-(.+)$/);
          if (!parts) return;

          const [, targetType, targetId] = parts;

          if (targetType === 'action') {
            const targetActionIndex = automation.actions.findIndex(
              (a) => String(a.id || automation.actions.indexOf(a)) === targetId
            );

            if (targetActionIndex !== -1) {
              // Clear parent_id and branch_type
              updatedActions[targetActionIndex] = {
                ...updatedActions[targetActionIndex],
                parent_id: null,
                branch_type: null,
              };
            }
          } else if (targetType === 'modifier' && automation.modifiers) {
            const targetModifierIndex = automation.modifiers.findIndex(
              (m) => String(m.id || automation.modifiers?.indexOf(m)) === targetId
            );

            if (targetModifierIndex !== -1) {
              // Clear parent_id and branch_type
              updatedModifiers[targetModifierIndex] = {
                ...updatedModifiers[targetModifierIndex],
                parent_id: null,
                branch_type: null,
              };
            }
          }
        });

        newAutomation.actions = updatedActions;
        newAutomation.modifiers = updatedModifiers;
        onUpdate(newAutomation);
      }

      // Apply visual changes from React Flow
      onEdgesChange(changes);
    },
    [automation, onUpdate, onEdgesChange]
  );

  const handleAddItem = (afterNodeId?: string) => {
    setAfterNodeId(afterNodeId);
    setPendingEdgeId(null); // Clear any pending edge
    setAllowedTypes(['trigger', 'action', 'modifier']); // Allow all types from main button
    setAddModalOpen(true);
  };

  const handleItemTypeSelected = (type: 'trigger' | 'action' | 'modifier', specificType: string) => {
    const insertionIndex = pendingEdgeId?.match(/^e-(\d+)$/)?.[1];
    setPendingItem({ type, specificType, afterNodeId, insertionIndex: insertionIndex === undefined ? undefined : Number(insertionIndex) });
    setAddModalOpen(false);
    setConfigModalOpen(true);
  };

  const handleConfigSave = (config: Record<string, unknown>) => {
    const newAutomation = { ...automation };
    const allItems = [...automation.triggers, ...automation.actions, ...(automation.modifiers || [])];
    const temporaryId = Math.min(0, ...allItems.map(item => item.id ?? 0)) - 1;
    const steps = [...automation.actions, ...(automation.modifiers || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const position = pendingItem?.insertionIndex ?? steps.length;
    const insertedScope = { parent_id: steps[position]?.parent_id ?? null, branch_type: steps[position]?.branch_type ?? null };
    const insertionOrder = position < steps.length ? (steps[position].order ?? position) : Math.max(-1, ...steps.map(item => item.order ?? 0)) + 1;
    if (!editingItem && pendingItem?.type !== 'trigger') {
      newAutomation.actions = automation.actions.map(item => ({ ...item, order: (item.order ?? 0) >= insertionOrder ? (item.order ?? 0) + 1 : item.order }));
      newAutomation.modifiers = (automation.modifiers || []).map(item => ({ ...item, order: (item.order ?? 0) >= insertionOrder ? (item.order ?? 0) + 1 : item.order }));
    }

    // Check if we're editing an existing item
    if (editingItem) {
      if (editingItem.type === 'trigger') {
        const updatedTriggers = [...newAutomation.triggers];
        updatedTriggers[editingItem.index] = {
          ...updatedTriggers[editingItem.index],
          config,
        };
        newAutomation.triggers = updatedTriggers;
      } else if (editingItem.type === 'action') {
        const updatedActions = [...newAutomation.actions];
        updatedActions[editingItem.index] = {
          ...updatedActions[editingItem.index],
          config,
        };
        newAutomation.actions = updatedActions;
      } else if (editingItem.type === 'modifier') {
        const updatedModifiers = [...(newAutomation.modifiers || [])];
        updatedModifiers[editingItem.index] = {
          ...updatedModifiers[editingItem.index],
          config,
        };
        newAutomation.modifiers = updatedModifiers;
      }
      setEditingItem(null);
    }
    // Otherwise we're adding a new item
    else if (pendingItem) {
      if (pendingItem.type === 'trigger') {
        const newTrigger: Partial<AutomationTrigger> = {
          id: temporaryId,
          trigger_type: pendingItem.specificType as AutomationTrigger['trigger_type'],
          config,
          order: newAutomation.triggers.length,
        };
        newAutomation.triggers = [...newAutomation.triggers, newTrigger as AutomationTrigger];
      } else if (pendingItem.type === 'action') {
        const newAction: Partial<AutomationAction> = {
          id: temporaryId,
          action_type: pendingItem.specificType as AutomationAction['action_type'],
          config,
          order: insertionOrder,
          ...insertedScope,
          go_to_node_id: null,
        };
        newAutomation.actions = [...newAutomation.actions, newAction as AutomationAction];
      } else if (pendingItem.type === 'modifier') {
        const newModifier: Partial<AutomationModifier> = {
          id: temporaryId,
          modifier_type: pendingItem.specificType as AutomationModifier['modifier_type'],
          config,
          order: insertionOrder,
          ...insertedScope,
        };
        newAutomation.modifiers = [...(newAutomation.modifiers || []), newModifier as AutomationModifier];
      }
      setPendingItem(null);
    }

    onUpdate(newAutomation);
    setConfigModalOpen(false);
    setPendingEdgeId(null);
  };

  return (
    <Box sx={{ width: '100%', height: '600px', position: 'relative' }}>
      {graphError && <Alert color="danger">{graphError}</Alert>}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        deleteKeyCode="Delete"
        edgesReconnectable={false}
        fitView
      >
        <Background />
        <Controls />
      </ReactFlow>

      {/* Floating Add Button */}
      <IconButton
        color="primary"
        size="lg"
        aria-label="Add workflow step"
        onClick={() => handleAddItem()}
        sx={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          borderRadius: '50%',
          width: 56,
          height: 56,
        }}
      >
        <AddIcon />
      </IconButton>

      {/* Add Item Modal - Select Type */}
      <AddWorkflowItemModal
        open={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setPendingEdgeId(null);
        }}
        onAdd={handleItemTypeSelected}
        allowedTypes={allowedTypes}
      />

      {/* Config Modal - Configure Immediately or Edit */}
      <WorkflowItemConfigModal
        open={configModalOpen && !!(pendingItem || editingItem)}
        onClose={() => {
          setConfigModalOpen(false);
          setPendingItem(null);
          setEditingItem(null);
        }}
        item={
          editingItem
            ? {
                type: editingItem.type,
                data: editingItem.item as AutomationTrigger | AutomationAction | AutomationModifier,
              }
            : pendingItem
            ? {
                type: pendingItem.type,
                data: {
                  trigger_type: pendingItem.type === 'trigger' ? pendingItem.specificType : undefined,
                  action_type: pendingItem.type === 'action' ? pendingItem.specificType : undefined,
                  modifier_type: pendingItem.type === 'modifier' ? pendingItem.specificType : undefined,
                  config: {},
                } as AutomationTrigger | AutomationAction | AutomationModifier,
              }
            : null
        }
        onSave={handleConfigSave}
      />
      
      {/* Move Position Modal */}
      {moveModalData && (
        <MovePositionModal
          open={moveModalOpen}
          onClose={() => {
            setMoveModalOpen(false);
            setMoveModalData(null);
          }}
          onConfirm={handleMoveConfirm}
          currentItem={moveModalData.item}
          workflowItems={[
            ...automation.actions.map((a, idx) => ({ 
              item: a, 
              type: 'action' as const, 
              order: a.order || idx 
            })),
            ...(automation.modifiers || []).map((m, idx) => ({ 
              item: m, 
              type: 'modifier' as const, 
              order: m.order || idx 
            })),
          ]}
          moveAll={moveModalData.moveAll}
        />
      )}
    </Box>
  );
}
